import set from 'lodash/set'
import get from 'lodash/get'
import omit from 'lodash/omit'
import isEmpty from 'lodash/isEmpty'
import type { FlattenedTree, TreeOptions, Tree } from './types'
import cloneDeep from 'lodash/cloneDeep'

// Metadata fields that should be removed from the output
const METADATA_FIELDS = ['_parentId', '_depth', '_path', '_hasChildren'] as const

type NodeWithChildren = Tree & { [key: string]: any }

export function rebuildTree<T extends Tree>(flat: FlattenedTree<T>[], options?: TreeOptions): T {
  const childrenKey = options?.childrenName ?? 'items'
  const idToNode: Record<string, NodeWithChildren> = {}
  const roots: NodeWithChildren[] = []
  const clonedFlat = cloneDeep(flat)

  // First pass: create all nodes without children
  for (const node of clonedFlat) {
    // Create a clean copy of the node without metadata fields
    const cleanNode = omit(node, METADATA_FIELDS) as NodeWithChildren
    idToNode[node._id] = cleanNode
    // Ensure children container exists
    set(cleanNode, childrenKey, [])
  }

  // Second pass: build the tree structure
  for (const node of clonedFlat) {
    const cleanNode = idToNode[node._id]
    const parentId = node._parentId

    if (parentId && idToNode[parentId]) {
      const parent = idToNode[parentId]
      const children = get(parent, childrenKey, []) as NodeWithChildren[]
      children.push(cleanNode)
      set(parent, childrenKey, children)
    } else {
      roots.push(cleanNode)
    }
  }

  // Clean up any remaining metadata that might have been added
  const cleanTree = (nodes: NodeWithChildren[]): T[] => {
    return nodes.map((node) => {
      // Create a clean copy without metadata fields
      const cleanNode = omit(node, METADATA_FIELDS) as Record<string, any>

      // Recursively clean children if they exist
      const children = get(node, childrenKey, []) as NodeWithChildren[]
      if (Array.isArray(children) && !isEmpty(children)) {
        set(cleanNode, childrenKey, cleanTree(children))
      } else {
        set(cleanNode, childrenKey, [])
      }

      return cleanNode as T
    })
  }

  const result = cleanTree(roots)

  //get only the first item
  return result?.[0]
}
